import { X } from 'lucide-react'
import { useId, useRef, type ReactNode, type RefObject } from 'react'

import OverlayMotion from '@/components/motion/OverlayMotion'
import IconButton from '@/components/ui/IconButton'
import type { PopoverTriggerProps } from '@/components/ui/Popover'
import { useModalDialog } from '@/components/ui/use-modal-dialog'
import { cn } from '@/lib/utils'

interface SheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /**
   * Renders the anchor. Spread the supplied props onto it, and return exactly
   * ONE element — the sheet reaches for it again to hand focus back.
   */
  trigger: (props: PopoverTriggerProps) => ReactNode
  /** Focused when the sheet opens. Falls back to the panel. */
  initialFocusRef?: RefObject<HTMLElement | null>
  /** Drawn in the header, and the sheet's accessible name. */
  title: string
  /** Sits between the title and the close button — MenuMulti's Clear. */
  headerAction?: ReactNode
  /**
   * False drops the grabber and the title row: the title only names the dialog for assistive
   * tech, and the body brings its own way out (the date picker's Cancel, mock `mkb-dp--sheet`).
   */
  header?: boolean
  className?: string
  children: ReactNode
}

/**
 * The same overlay as Popover, for the widths where an anchored panel is the
 * wrong shape: a sheet that rises from the bottom edge, full-bleed, with a
 * grabber and a title of its own.
 *
 * Props are Popover's deliberately — `open`, `onOpenChange`, `trigger`,
 * `initialFocusRef`, `children` — so a menu can hand the same render function
 * and the same body to either shell and change nothing else.
 *
 * A native `<dialog>` opened with `showModal()`, driven by the same
 * useModalDialog that Modal runs. It lands in the top layer, which is what
 * makes the portal Popover needs unnecessary here — no ancestor's `overflow`,
 * z-index or stacking context can reach it.
 *
 * A menu's small-screen shell, not a dialog for content: anything that asks
 * the member something is a Modal.
 */
export default function Sheet({
  open,
  onOpenChange,
  trigger,
  initialFocusRef,
  title,
  headerAction,
  header = true,
  className,
  children,
}: SheetProps) {
  const panelId = useId()
  const titleId = useId()
  const anchorRef = useRef<HTMLSpanElement | null>(null)
  const { dialogRef, backdropProps } = useModalDialog({
    open,
    onOpenChange,
    initialFocusRef,
    returnFocus: () => {
      const anchor = anchorRef.current?.firstElementChild
      return anchor instanceof HTMLElement ? anchor : null
    },
  })

  const triggerProps: PopoverTriggerProps = {
    'aria-expanded': open,
    // Pointing at an id that is not in the document is worse than saying
    // nothing, and the panel only exists while open.
    'aria-controls': open ? panelId : undefined,
    // Not Popover's caller-chosen value: whatever the body holds, what opens
    // at this width is a dialog.
    'aria-haspopup': 'dialog',
    onClick: () => {
      onOpenChange(!open)
    },
    onKeyDown: (event) => {
      if (open || (event.key !== 'ArrowDown' && event.key !== 'ArrowUp')) return
      event.preventDefault()
      onOpenChange(true)
    },
  }

  return (
    <>
      <span ref={anchorRef} className="contents">
        {trigger(triggerProps)}
      </span>
      <OverlayMotion
        as="dialog"
        kind="sheet"
        ref={dialogRef}
        id={panelId}
        // Both are what `showModal()` already conveys. Written out so the
        // element's semantics are stated by the component rather than left to
        // a host-language mapping.
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        {...backdropProps}
        className={cn(
          // `top-auto` rather than the UA's `inset: 0` plus an auto margin:
          // with the top edge released the box keeps its content height and
          // the bottom edge is pinned outright, no margin arithmetic to lose.
          // `hidden open:flex`, never a bare `flex`. The UA sheet hides a
          // closed dialog with `dialog:not([open]) { display: none }`, but that
          // is a user-agent rule and ANY author-origin `display` beats it
          // regardless of specificity. A bare `flex` therefore leaves every
          // closed sheet painted, full-size and focusable — two of them were
          // sitting on top of each other at 375px with 23 reachable controls
          // between them, and `aria-modal="true"` on a visible non-modal tells
          // a screen reader to ignore the rest of the page.
          'fixed inset-x-0 top-auto bottom-0 m-0 hidden max-h-[85dvh] w-full max-w-none flex-col open:flex',
          'rounded-t-lg border-0 bg-popover text-popover-foreground',
          // 55%: Modal's Figma-cited scrim, so every overlay dims the page alike.
          // `--space-4`, not a literal 1rem, so the value stays a token reference.
          'p-0 px-4 pb-[calc(var(--space-4)+var(--safe-bottom))] backdrop:bg-black/55',
          className,
        )}
      >
        {header ? (
          <>
            {/* 10 → mt-2 (4-grid, nearest-8 tie-break). */}
            <div
              aria-hidden="true"
              className="mx-auto mt-2 h-1 w-9 shrink-0 rounded-full bg-border-strong"
            />

            {/* 44px rather than the 28px the frame draws: Clear and Close sit in
              this row, and a thumb target is what the row is really sized by. */}
            <div className="mt-3 flex min-h-11 shrink-0 items-center gap-2">
              <h2 id={titleId} className="min-w-0 flex-1 truncate text-h-m" title={title}>
                {title}
              </h2>
              {headerAction}
              <IconButton
                aria-label={`Close ${title}`}
                onClick={() => {
                  onOpenChange(false)
                }}
                className="group/close bg-muted"
              >
                {/* 18 → size-4 (4-grid, nearest-8 tie-break). */}
                <X
                  className="size-4 transition-[rotate] duration-med ease-out group-hover/close:motion-safe:rotate-90 group-focus-visible/close:motion-safe:rotate-90"
                  aria-hidden="true"
                />
              </IconButton>
            </div>
          </>
        ) : (
          <h2 id={titleId} className="sr-only">
            {title}
          </h2>
        )}

        {/* `flex-auto` (flex: 1 1 auto), not `flex-1` (flex: 1 1 0%): this
            dialog has no fixed height, only the `max-h-[85dvh]` cap above.
            WebKit sizes a fit-content column flex container from each item's
            FLEX BASIS, and `flex-1`'s basis is 0 — this body would
            contribute nothing and the sheet would collapse to grabber +
            title. `flex-auto`'s basis is the content size in both engines;
            `min-h-0` still lets it shrink and scroll once the cap binds.

            It scrolls itself, as Modal's body does, so a body with no
            scroller of its own scrolls under the title and Close rather than
            taking them with it. The 4px of padding, handed back by the
            negative margins, is room for a field's focus ring, which the
            overflow would otherwise clip. */}
        <div
          className={cn(
            '-mx-1 -mb-1 flex min-h-0 flex-auto flex-col overflow-y-auto overscroll-contain scrollbar-slim p-1',
            header ? 'mt-2' : 'mt-3',
          )}
        >
          {children}
        </div>
      </OverlayMotion>
    </>
  )
}
